import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { downloadBlob } from "../../lib/downloadBlob";
import { asciiName, versionLine } from "../../lib/portalPresence";
import { longDate, whenLabel } from "../../lib/portalPortfolio";
import { Chip } from "./PortalShell";
import CompositionBoard from "./CompositionBoard";

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] disabled:opacity-50";
const field = "w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20";
const REPORTS = [["platform", "Raportul generat de platformă (Google Meet)"], ["manual", "Listă întocmită de echipă"]];

/**
 * The final evaluation in the committee's plenary, as the team runs it
 * (portal PR 11a): the decision constituting the committee (Anexa 7), who was
 * present (Anexa 10.2), then the second round of scores on the same grid
 * (Anexele 11 and 12). The strategy's conditions — 3 present at once, over by
 * 20:00, the date in its window — are said, never enforced.
 */
export default function FinalBoard({ groupId }) {
  const [kind, setKind] = useState("main");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/admin/portal/groups/${groupId}/final`, { params: { session: kind } })).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca susținerea."));
    }
  }, [groupId, kind]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-[#c0483f]" role="alert">{error}</p>;
  if (!data) return <Loader2 className="mx-auto animate-spin text-[#6B7280]" />;
  const ev = data.evaluation;
  const a = data.attendance;

  return (
    <div className="grid gap-8" data-testid="final-board">
      {data.scoring.has_resit && (
        <div className="inline-flex w-fit overflow-hidden rounded-full border border-[#E5E7EB] bg-white" role="tablist">
          {[["main", "Sesiunea principală"], ["resit", "Sesiunea de restanță"]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
              className={`px-4 py-1.5 text-sm font-semibold ${kind === k ? "bg-[#373A45] text-white" : "text-[#373A45] hover:bg-[#F5F6F6]"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      <section aria-labelledby="fn-when">
        <h2 id="fn-when" className="font-display text-xl font-bold text-[#373A45]">Susținerea în plenul comisiei</h2>
        <p className="mt-1 text-sm text-[#373A45]">
          {ev.evaluation_date ? longDate(ev.evaluation_date) : "Data nu e trecută"}
          {ev.evaluation_start && `, ${ev.evaluation_start}–${ev.evaluation_end || "…"}`}
          {ev.evaluation_place && ` · ${ev.evaluation_place}`}
          <span className="ml-1 text-xs text-[#6B7280]">(data, ora și platforma se trec la „Datele grupei din antetul anexelor”, în Prezență)</span>
        </p>
        {a.warnings.map((w) => (
          <p key={w} className="mt-2 flex gap-2 rounded-xl bg-[#d99a2b]/10 px-3 py-2 text-sm text-[#8a5d12]">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {w}
          </p>
        ))}
      </section>

      <Docs title="Anexa 7 — Decizia de constituire a comisiei" what="annex_7" data={data} groupId={groupId} kind={kind}
        ready={data.annex_7.ready} reasons={data.annex_7.reasons} onChanged={setData}
        hint="Se generează înainte de susținere, din comisie, din datele grupei și din „Antetul anexelor” (reprezentantul legal, actul de numire)." />

      <Attendance data={data} groupId={groupId} kind={kind} onChanged={setData} />

      <Docs title="Anexa 10.2 — Raportul de prezență la evaluarea finală" what="annex_10" data={data} groupId={groupId}
        kind={kind} ready={a.annex_10.ready} reasons={a.annex_10.reasons} onChanged={setData}
        hint="Generată, îngheață lista: la punctaj intră doar cei trecuți prezenți. Cei absenți devin restanțieri (art. 29)." />

      <CompositionBoard groupId={groupId} round="final" session={kind} key={`${kind}-${data.documents.length}`} />

      {kind === "main" && <Closing data={data} groupId={groupId} onChanged={setData} onRefresh={load} />}
    </div>
  );
}

/** PR 11b — the minutes (Anexa 13), the figures and deadline of the
 *  notification to the ministry (Anexa 14), and the certificates' list. */
function Closing({ data, groupId, onChanged, onRefresh }) {
  const c = data.closing;
  const [busy, setBusy] = useState(false);
  return (
    <section aria-labelledby="fn-closing" className="grid gap-6 border-t border-[#E5E7EB] pt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="fn-closing" className="font-display text-2xl font-bold text-[#373A45]">Închiderea</h2>
        <button type="button" className={quiet} onClick={onRefresh}>Recalculează</button>
      </div>
      {!c.ready && (
        <p className="text-sm text-[#8a5d12]">Se calculează după ce sunt generate: {c.missing.join(", ")}.</p>
      )}
      {c.warnings.map((w) => (
        <p key={w} className="flex gap-2 rounded-xl bg-[#d99a2b]/10 px-3 py-2 text-sm text-[#8a5d12]">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {w}
        </p>
      ))}

      <Docs title="Anexa 13 — Procesul-verbal" what="annex_13" data={data} groupId={groupId} kind="main"
        ready={c.ready} reasons={c.ready ? [] : [`Lipsesc: ${c.missing.join(", ")}.`]} onChanged={onChanged}
        hint="Textul modelului, cu tabelul 3 calculat din Anexele 5, 6, 9 și 12. Litera de la art. 14 alin. (5) din metodologie rămâne de completat de mână." />

      {c.ready && (
        <section aria-labelledby="fn-notice">
          <h2 id="fn-notice" className="font-display text-xl font-bold text-[#373A45]">Anexa 14 — cifrele pentru notificare</h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Notificarea o completezi în modelul ministerului și în formularul Google Forms, apoi o trimiți scanată, semnată și
            ștampilată, la dfc_certificare@formare.edu.ro.
            {c.notice_due && <> Termen: <strong className="text-[#373A45]">{longDate(c.notice_due)}</strong> (15 zile de la evaluare, art. 33).</>}
          </p>
          <div className={`mt-3 overflow-x-auto ${card}`}>
            <table className="w-full min-w-[28rem] text-sm" data-testid="fn-notice">
              <tbody className="divide-y divide-[#E5E7EB]">
                {c.columns.map((col) => (
                  <tr key={col.col}>
                    <td className="w-14 px-4 py-2 font-mono text-xs text-[#6B7280]">{col.col}.</td>
                    <td className="px-2 py-2 text-[#373A45]">{col.label}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums text-[#373A45]">{col.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {c.ready && (
        <section aria-labelledby="fn-promoted">
          <h2 id="fn-promoted" className="font-display text-xl font-bold text-[#373A45]">Certificatele</h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Lista promovaților cu tot ce cere certificatul (Anexa 15): numele cu inițiala tatălui, funcția, specializarea,
            unitatea, localitatea, județul, perioada și data evaluării. Certificatele se eliberează în cel mult 30 de zile.
          </p>
          <button type="button" className={`${quiet} mt-3`} disabled={busy} data-testid="fn-promoted"
            onClick={() => downloadBlob(`/admin/portal/groups/${groupId}/final/promoted.xlsx`,
              `Promovati_${asciiName(data.scoring.group.name)}.xlsx`, setBusy)}>
            <Download size={12} /> Lista promovaților (Excel)
          </button>
        </section>
      )}
    </section>
  );
}

function Attendance({ data, groupId, kind, onChanged }) {
  const a = data.attendance;
  const [marks, setMarks] = useState(() => Object.fromEntries(a.rows.map((r) => [r.enrollment_id, r.status])));
  const [reason, setReason] = useState("");
  const [report, setReport] = useState(a.report_kind || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setMarks(Object.fromEntries(a.rows.map((r) => [r.enrollment_id, r.status])));
    setReport(a.report_kind || "");
  }, [a]);

  const dirty = a.rows.some((r) => (marks[r.enrollment_id] || null) !== (r.status || null));
  const call = async (fn, ok) => {
    setBusy(true);
    try {
      onChanged((await fn()).data);
      toast.success(ok);
      setReason("");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };
  const save = () => call(() => api.put(`/admin/portal/groups/${groupId}/final/attendance`, {
    session: kind, marks: Object.fromEntries(Object.entries(marks).filter(([, v]) => v)), reason,
  }), "Am salvat prezența.");
  const confirm = () => call(() => api.post(`/admin/portal/groups/${groupId}/final/attendance/confirm`, {
    session: kind, report_kind: report,
  }), "Prezența e confirmată.");

  return (
    <section aria-labelledby="fn-att">
      <h2 id="fn-att" className="font-display text-xl font-bold text-[#373A45]">Prezența la susținere</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Sunt chemați cei admiși în Anexa 9. Marchează-i pe baza raportului platformei, apoi confirmă.
        {a.confirmed_at && ` Confirmată de ${a.confirmed_by?.name} la ${whenLabel(a.confirmed_at)}.`}
      </p>
      {!data.attendance.annex_9 && <p className="mt-2 text-sm text-[#8a5d12]">Anexa 9 nu e generată: nu se știe încă cine e chemat.</p>}
      {a.rows.length > 0 && (
        <div className={`mt-3 ${card}`} data-testid="fn-attendance">
          <ul className="divide-y divide-[#E5E7EB]">
            {a.rows.map((r) => (
              <li key={r.enrollment_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                <span className="font-semibold text-[#373A45]">{r.name}</span>
                <div className="inline-flex overflow-hidden rounded-full border border-[#E5E7EB]" role="radiogroup" aria-label={`Prezența: ${r.name}`}>
                  {[["present", "Prezent"], ["absent", "Absent"]].map(([v, label]) => (
                    <button key={v} type="button" role="radio" aria-checked={marks[r.enrollment_id] === v}
                      onClick={() => setMarks((m) => ({ ...m, [r.enrollment_id]: v }))} data-testid={`fn-mark-${r.enrollment_id}-${v}`}
                      className={`px-3 py-1 text-xs font-semibold ${marks[r.enrollment_id] === v
                        ? (v === "present" ? "bg-[#519872] text-white" : "bg-[#c0483f] text-white") : "hover:bg-[#F5F6F6]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <div className="grid gap-2 border-t border-[#E5E7EB] p-4">
            <p className="text-xs text-[#6B7280]">{a.present} prezenți · {a.absent} absenți</p>
            {a.confirmed_at && dirty && (
              <input className={field} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="De ce corectezi prezența confirmată (rămâne consemnat)" aria-label="Motivul corecturii" />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={quiet} disabled={busy || !dirty || (a.confirmed_at && !reason.trim())} onClick={save}
                data-testid="fn-save">
                Salvează prezența
              </button>
              {!a.confirmed_at && (
                <>
                  <select className="rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs text-[#373A45]" value={report}
                    onChange={(e) => setReport(e.target.value)} aria-label="De unde vine lista">
                    <option value="">De unde vine lista?</option>
                    {REPORTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                  <button type="button" className={quiet} disabled={busy || dirty || !report || a.rows.some((r) => !r.status)}
                    onClick={confirm} data-testid="fn-confirm">
                    Confirmă prezența
                  </button>
                </>
              )}
              {a.confirmed_at && <Chip tone="good">Confirmată</Chip>}
            </div>
            {a.corrections.map((c) => (
              <p key={c.at} className="text-xs text-[#6B7280]">Corectată de {c.by?.name} la {whenLabel(c.at)}: {c.reason}</p>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Docs({ title, hint, what, data, groupId, kind, ready, reasons, onChanged }) {
  const [registry, setRegistry] = useState({ no: "", date: "" });
  const [busy, setBusy] = useState(false);
  const n = what.replace("annex_", "");
  const label = n === "10" ? "10.2" : n;
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/portal/groups/${groupId}/final-documents`, {
        session: kind, kind: what, registry_no: registry.no, registry_date: registry.date });
      toast.success(`Anexa ${label}, versiunea ${r.data.document.version}, e generată.`);
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut genera documentul."));
    } finally {
      setBusy(false);
    }
  };
  const versions = data.documents.filter((d) => d.kind === what);
  return (
    <section aria-label={title}>
      <h2 className="font-display text-xl font-bold text-[#373A45]">{title}</h2>
      <p className="mt-1 text-sm text-[#6B7280]">{hint}</p>
      <div className={`mt-3 grid gap-3 p-4 ${card}`} data-testid={`fn-${what}`}>
        {!ready && <ul className="list-disc pl-5 text-xs text-[#8a5d12]">{reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
          <input className={field} value={registry.no} onChange={(e) => setRegistry({ ...registry, no: e.target.value })}
            placeholder="Nr. de înregistrare (opțional)" aria-label="Numărul din registru" />
          <input className={field} value={registry.date} onChange={(e) => setRegistry({ ...registry, date: e.target.value })}
            placeholder="din data (opțional)" aria-label="Data din registru" />
          <button type="button" className={quiet} disabled={busy || !ready} onClick={generate} data-testid={`fn-${what}-generate`}>
            {busy && <Loader2 size={13} className="animate-spin" />} Generează Anexa {label}
          </button>
        </div>
        {versions.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className={d.latest ? "font-semibold text-[#373A45]" : "text-[#6B7280]"}>
              {versionLine(d)}{d.registry_no ? ` · nr. ${d.registry_no}` : ""}
            </span>
            {d.signed && <Chip tone="good">semnată</Chip>}
            {d.latest && d.stale && <Chip tone="warn">datele s-au schimbat</Chip>}
            {["pdf", "xlsx"].map((fmt) => (
              <button key={fmt} type="button" className="inline-flex items-center gap-1 font-semibold text-[#965EA7] hover:underline"
                onClick={() => downloadBlob(`/admin/portal/documents/${d.id}/${fmt}`,
                  `Anexa_${n}_${asciiName(data.scoring.group.name)}_${kind}_v${d.version}.${fmt}`)}>
                <Download size={12} /> {fmt === "pdf" ? "PDF" : "Excel"}
              </button>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
